import { useAuthData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';

import { smartConversationApi } from 'components/conversation/components/integrations/elevio/src/apis/rest/elevio/smartConversation.api';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { elevioActions } from '../../elevio.actions';
import { elevioSelectors } from '../../elevio.reducer/selectors';

export type SmartReplyVariablesV2 = {
  articleBody: string;
  conversationId: number;
};

type UseSmartReplyResponsePayload = {
  getSmartReplyResponse: () => void;
  response: {
    data: Pick<SmartReplyResponsePayloadV2, 'response'> | null;
    loading: boolean;
  };
};

export type SmartReplyResponsePayloadV2 = {
  response: string;
  token_usage: string;
};

type UseSmartReplyResponseParams = {
  articleId: string | null;
  articleBody: string;
  conversationId: number | null;
};
export function useSmartReplyResponse({
  articleId,
  articleBody,
  conversationId,
}: UseSmartReplyResponseParams): UseSmartReplyResponsePayload {
  const dispatch = useAppDispatch();
  const data = useAppSelector(elevioSelectors.smartReply.data);
  const loading = useAppSelector(elevioSelectors.smartReply.loading);
  const authData = useAuthData();
  const accessToken = authData?.access_token;

  const [mutationV2, { error: smartConversationResponseError }] =
    smartConversationApi.usePostAiApiBetaConversationsSmartResponsesMutation();

  async function getAiResponseV2(params: SmartReplyVariablesV2): Promise<void> {
    dispatch(elevioActions.smartReply.smartReplyRequested());

    try {
      if (!accessToken)
        throw new Error('getAiResponseV2 > No access token found');

      const smartConversationResponse = await mutationV2({
        conversationResponseInput: {
          article_text: params.articleBody,
          csid: params.conversationId,
        },
      });

      if (!smartConversationResponse)
        throw new Error('Smart Conversation Response has an invalid format');

      if ('error' in smartConversationResponse)
        throw new Error((smartConversationResponse.error as any).message);

      dispatch(
        elevioActions.smartReply.smartReplyResponseReceived({
          data: smartConversationResponse.data,
        }),
      );
    } catch (error) {
      dispatch(elevioActions.smartReply.smartReplyResponseError());
      console.error(smartConversationResponseError);
      console.error(error);
    } finally {
      dispatch(elevioActions.smartReply.smartReplyFinally());
    }
  }

  return {
    getSmartReplyResponse: () => {
      if (articleId && conversationId) {
        ampli.smartConversationReplyButtonClicked({
          articleId,
          conversationId,
          knowledgeSource: 'elevio',
        });
        getAiResponseV2({
          conversationId,
          articleBody,
        });
      }
    },
    response: {
      data,
      loading,
    },
  };
}
